// Business logic: Questionnaire store manages all questionnaire completion state with localStorage persistence.
// This store tracks which questionnaires users have answered to prevent duplicate submissions
// when isFillableOnce is true and ensures proper UI state management.
// Persists across browser sessions to maintain questionnaire completion integrity.

import { create } from "zustand";
import { persist } from "zustand/middleware";

interface QuestionnaireStore {
  // Array of questionnaire IDs that the user has already answered
  answeredQuestionnaires: number[];
  
  // Actions to manage questionnaire state
  setAnsweredQuestionnaires: (answeredQuestionnaires: number[]) => void;
  addAnsweredQuestionnaire: (questionnaireId: number) => void;
  hasAnsweredQuestionnaire: (questionnaireId: number) => boolean;
  clearAnsweredQuestionnaires: () => void;
}

export const useQuestionnaireStore = create<QuestionnaireStore>()(
  persist(
    (set, get) => ({
      // State: Track questionnaires the user has already answered
      answeredQuestionnaires: [],
      
      // Action: Set the entire answered questionnaires array (useful for initial load from server)
      setAnsweredQuestionnaires: (answeredQuestionnaires: number[]) => 
        set({ answeredQuestionnaires }),
      
      // Action: Add a single questionnaire ID to answered questionnaires after successful submission
      addAnsweredQuestionnaire: (questionnaireId: number) => 
        set((state) => ({ 
          answeredQuestionnaires: [...state.answeredQuestionnaires, questionnaireId] 
        })),
      
      // Selector: Check if user has answered a specific questionnaire
      hasAnsweredQuestionnaire: (questionnaireId: number) => 
        get().answeredQuestionnaires.includes(questionnaireId),
      
      // Action: Clear all answered questionnaires (useful for testing or user logout)
      clearAnsweredQuestionnaires: () => 
        set({ answeredQuestionnaires: [] }),
    }),
    {
      name: "nsc-questionnaire-storage", // localStorage key
      // No partialize needed - persist the entire questionnaire store
    }
  )
);